import { Badge, Detail } from '../../components/ui'
import type { AvailabilityWindow, Listing, ListingSource } from '../../api/objects'
import { t } from '../../i18n'
import { useSession } from '../../auth/session'
import { dayOnly, fullTime, months } from '../../format'
import { listingStatusLabel, sourceTypeLabel } from '../../labels'
import { AvailabilityForm } from './AvailabilityForm'
import { FreshnessLine } from './Freshness'
import { InfoNeeded } from './InfoNeeded'
import { ListingHistory } from './ListingHistory'
import { MediaPanel } from './MediaPanel'
import { PriceForm } from './PriceForm'
import { PriceTable } from './PriceTable'
import { StatusPicker } from './StatusPicker'
import { VerifyPanel } from './VerifyForm'

/**
 * One commercial offer on one unit.
 *
 * The order is the order a manager reads it in before answering a client: may I
 * offer this at all, what does it cost, when is it free, who is letting it. The
 * two questions that decide whether anything here may be repeated to somebody —
 * the freshness of the stored check and the completeness of the price — come
 * first, and neither is a quiet grey line.
 */
export function ListingCard({
  propertyId,
  listing,
  now,
}: {
  readonly propertyId: string
  readonly listing: Listing
  /** Injected by the checks so that "expired" does not depend on the day they run. */
  readonly now?: Date | undefined
}) {
  const { can } = useSession()

  return (
    <article className={listing.offerable ? 'listing listing--offerable' : 'listing'}>
      <header className="listing__head">
        <h4 className="listing__title">{listing.title === '' ? t('listing.title') : listing.title}</h4>
        <div className="listing__badges">
          <Badge tone={listing.offerable ? 'accent' : 'muted'}>{listingStatusLabel(listing.status)}</Badge>
          {/*
            Spelled out beside the status because they are not the same fact:
            'verified' is a status, and whether this may go in front of a client
            is that status plus a check that has not run out (ADR 0008).
          */}
          <Badge tone={listing.offerable ? 'neutral' : 'warning'}>
            {listing.offerable ? t('listing.offerable') : t('listing.notOfferable')}
          </Badge>
          {listing.sourceType === '' ? null : (
            <Badge tone="muted">{sourceTypeLabel(listing.sourceType)}</Badge>
          )}
        </div>
      </header>

      {listing.statusReason === '' ? null : (
        <p className="listing__reason">{t('listing.statusReason', { reason: listing.statusReason })}</p>
      )}

      <FreshnessLine listing={listing} now={now} />

      {/*
        Directly under the freshness line, because they answer neighbouring
        questions: one says whether this may be offered, the other says what we
        would be unable to answer if it were.
      */}
      <InfoNeeded listing={listing} propertyId={propertyId} />

      <section className="listing__section">
        <h5 className="listing__section-title">{t('listing.price')}</h5>
        <PriceTable components={listing.priceComponents} />
      </section>

      <section className="listing__section">
        <h5 className="listing__section-title">{t('listing.availability')}</h5>
        {listing.availabilityWindows.length === 0 ? (
          <p className="panel-block__empty">{t('listing.availabilityEmpty')}</p>
        ) : (
          <ul className="windows">
            {listing.availabilityWindows.map((window) => (
              <Window key={window.id} window={window} />
            ))}
          </ul>
        )}
        {/*
          Entering it by hand. Without this every flat entered here carried
          "nobody has recorded when it is free" for ever, and the matcher will
          not promise a date nobody wrote down.
        */}
        <AvailabilityForm
          listingID={listing.id}
          propertyID={propertyId}
          windows={listing.availabilityWindows}
        />
      </section>

      <section className="listing__section">
        <h5 className="listing__section-title">{t('listing.terms')}</h5>
        <dl className="details details--tight">
          {/*
            The legal type of the Italian contract as the source stated it. Never
            derived from our own term band and never inferred (invariant 4), so
            "nobody told us" is shown as exactly that.
          */}
          <Detail term={t('listing.contractType')}>
            {listing.contractTypeDeclared ?? <NotAsked />}
          </Detail>
          <Detail term={t('listing.contractRegistered')}>
            <ThreeState value={listing.contractRegistered} />
          </Detail>
          <Detail term={t('listing.residenza')}>
            <ThreeState value={listing.residenzaPossible} />
          </Detail>
        </dl>
      </section>

      <section className="listing__section">
        <h5 className="listing__section-title">{t('listing.sources')}</h5>
        {listing.sources.length === 0 ? (
          <p className="panel-block__empty">{t('listing.sourcesEmpty')}</p>
        ) : (
          <ul className="sources">
            {listing.sources.map((source) => (
              <SourceRow key={source.id} source={source} />
            ))}
          </ul>
        )}
      </section>

      {listing.notes === '' ? null : <p className="listing__notes">{listing.notes}</p>}

      {can('listings.verify') ? <VerifyPanel propertyId={propertyId} listingId={listing.id} /> : null}

      {can('listings.write') ? (
        <>
          <PriceForm
            listingID={listing.id}
            propertyID={propertyId}
            components={listing.priceComponents}
          />
          <MediaPanel listingID={listing.id} propertyID={propertyId} assets={listing.media} />
          <StatusPicker
            listingID={listing.id}
            current={listing.status}
            propertyID={propertyId}
          />
        </>
      ) : null}

      <ListingHistory listingId={listing.id} />
    </article>
  )
}

/** Nobody has asked. Not a no — the difference decides whether to ask. */
function NotAsked() {
  return <span className="not-asked">{t('unit.notAsked')}</span>
}

function ThreeState({ value }: { readonly value: boolean | undefined }) {
  if (value === undefined) return <NotAsked />
  return <>{value ? t('common.yes') : t('common.no')}</>
}

function Window({ window }: { readonly window: AvailabilityWindow }) {
  const min = months(window.minMonthsHundredths)
  const max = months(window.maxMonthsHundredths)

  return (
    <li className="window">
      <span className="window__dates">
        {t('listing.windowFrom', { from: dayOnly(window.from) })}{' '}
        {/*
          Both ends inclusive: `to` is the last day covered, not the day the next
          tenant arrives (docs/adr/0004). An empty end is open-ended, which is the
          usual answer for a long let and is not the same as unknown.
        */}
        {window.to === undefined
          ? t('listing.windowOpenEnded')
          : t('listing.windowTo', { to: dayOnly(window.to) })}
      </span>
      <span className="window__limits">
        {min === undefined ? null : <span>{t('listing.windowMin', { months: min })}</span>}
        {max === undefined ? null : <span>{t('listing.windowMax', { months: max })}</span>}
        {window.renewable === undefined ? null : (
          <span>
            {t('listing.renewable')}: {window.renewable ? t('common.yes') : t('common.no')}
          </span>
        )}
        {window.moveInFlexibilityDays > 0 ? (
          <span>{t('listing.moveInFlexibility', { days: window.moveInFlexibilityDays })}</span>
        ) : null}
      </span>
      {window.note === '' ? null : <span className="window__note">{window.note}</span>}
    </li>
  )
}

function SourceRow({ source }: { readonly source: ListingSource }) {
  return (
    <li className="source">
      <span className="source__name">
        {source.url === '' ? (
          source.sourceName
        ) : (
          // `noreferrer` as well as `noopener`: the portals are third parties and
          // there is no reason to tell them which of our screens a click came from.
          <a href={source.url} target="_blank" rel="noreferrer noopener">
            {source.sourceName === '' ? source.url : source.sourceName}
          </a>
        )}
      </span>
      <span className="source__meta">
        {source.sourceType === '' ? null : <span>{sourceTypeLabel(source.sourceType)}</span>}
        {source.lastSeenAt === undefined ? null : (
          <span>{t('listing.sourceLastSeen', { time: fullTime(source.lastSeenAt) })}</span>
        )}
        {/*
          What the source says we may do to reach the publisher. Writing first to
          a scraped number is the behaviour that gets the account banned, so it is
          on screen as a permission rather than left to be assumed.
        */}
        {source.permittedContactMethod === '' ? null : (
          <span>{t('listing.sourceContact', { method: source.permittedContactMethod })}</span>
        )}
      </span>
      {source.disappearedAt === undefined ? null : (
        <span className="source__gone">
          {t('listing.sourceDisappeared', { time: fullTime(source.disappearedAt) })}
        </span>
      )}
    </li>
  )
}
